import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Antaryatra';
const BASE_URL = 'https://www.antaryatra.org';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  'Book Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR — or have your ritual performed at our sacred Kashi site. Griha Pravesh, Marriage, Dosha Nivaran & more.';

interface SEOHeadProps {
  /** Page title – will be appended with " | Antaryatra" */
  title: string;
  description?: string;
  /** Canonical path, e.g. "/service/shiv-pujan" (no trailing slash) */
  canonicalPath?: string;
  ogImage?: string;
  /** "website" | "article" | "product" – defaults to "website" */
  ogType?: string;
  /** Additional keywords beyond the site-wide defaults */
  keywords?: string;
  /** Pass false on private/auth pages to prevent indexing */
  noIndex?: boolean;
  /** JSON-LD structured data object(s) */
  structuredData?: object | object[];
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = '/',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  keywords,
  noIndex = false,
  structuredData,
}) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalURL = `${BASE_URL}${canonicalPath}`;
  const defaultKeywords =
    'online puja booking, pandit booking, Vedic rituals, kashi pandits, puja services India';
  const allKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  const renderStructuredData = () => {
    if (!structuredData) return null;
    const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
    return dataArray.map((data, i) => (
      <script key={i} type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    ));
  };

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={canonicalURL} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalURL} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@antaryatra" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {renderStructuredData()}
    </Helmet>
  );
};

export default SEOHead;
